import {
  useFormState,
  SectionProgressSummary,
} from '../components/container/forms/hooks/useFormState';
import { SectionKey } from '../types';
import { TagOption } from '@tw/tw-component-library';
import { FormContext } from './useFormContext';
import { useMemo } from 'react';
import { FormState } from '../components/container/forms/types';

export interface FormAdditionalProps {
  tagPickerSuggestions: Record<string, TagOption[]>;
  loadingFields: Record<string, boolean>;
  minSearchLength: number;
}

export interface FormContextType {
  formState: FormState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateFormData: (data: Record<string, any>) => void;
  setErrors: (errors: Record<string, string>) => void;
  resetForm: () => void;
  resetRejectionStatus: () => void;
  completeSection: (section: SectionKey, completedDate?: string) => void;
  submitSection: (section: SectionKey) => void;
  setCurrentSection: (section: SectionKey) => void;
  canProceedToSection: (section: SectionKey) => boolean;
  sectionProgress: SectionProgressSummary;
  updateSectionProgress: (
    sectionKey: SectionKey,
    isComplete: boolean,
    completedDate?: string
  ) => void;
  updateTagPicker?: (fieldName: string, value: string) => Promise<void>;
  updateSectionState: (
    currentSection: SectionKey,
    nextSection: SectionKey | null,
    isComplete?: boolean
  ) => void;
  getCurrentSectionFlow: () => SectionKey[];
  handleSectionFlowChange: () => void;
}

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    formState,
    updateFormData,
    setErrors,
    resetForm,
    resetRejectionStatus,
    completeSection,
    submitSection,
    setCurrentSection,
    canProceedToSection,
    sectionProgress,
    updateSectionProgress,
    updateSectionState,
    getCurrentSectionFlow,
    handleSectionFlowChange,
  } = useFormState();

  const contextValue = useMemo(
    () => ({
      formState,
      updateFormData,
      setErrors,
      resetForm,
      resetRejectionStatus,
      completeSection,
      submitSection,
      setCurrentSection,
      canProceedToSection,
      sectionProgress,
      updateSectionProgress,
      updateSectionState,
      getCurrentSectionFlow,
      handleSectionFlowChange,
    }),
    [
      formState,
      updateFormData,
      setErrors,
      resetForm,
      resetRejectionStatus,
      completeSection,
      submitSection,
      setCurrentSection,
      canProceedToSection,
      sectionProgress,
      updateSectionProgress,
      updateSectionState,
      getCurrentSectionFlow,
      handleSectionFlowChange,
    ]
  );

  return (
    <FormContext.Provider value={contextValue as unknown as FormContextType}>
      {children}
    </FormContext.Provider>
  );
};
